import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  IconButton,
  Divider,
  Card,
  CardContent,
  useTheme,
  alpha,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Switch,
  Slider,
  Autocomplete,
  Radio,
  RadioGroup,
  FormLabel,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import RichTextEditor from "../../../../components/RichTextEditor/RichTextEditor";
import 'react-quill-new/dist/quill.snow.css';
import jobseekerapi from '../../../../services/jobseekerApi';
import {
  Group as AllIcon,
  Person as SpecificIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Code as VariableIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  Send as SendIcon,
  NotificationsActive as ReminderIcon,
  ContentCopy as CopyIcon,
  List as ListIcon,
  SmartButton as ButtonIcon, // Keep ButtonIcon for the integrated panel
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format } from 'date-fns';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import emailTemplateService, { EmailTemplate as ApiEmailTemplate } from '../../../../services/emailTemplateService';
import { timeZoneList } from '../../../../utils/timezones';
import leadService, { Lead, LeadCategory } from '../../../../services/lead.service';

// Types
// Types
interface EmailTemplate extends Omit<ApiEmailTemplate, 'id'> {
  id: string; // Use string for frontend compatibility, maps to _id
}

// Removed interface Candidate as it's no longer used

interface Recipient {
  id: string;
  email: string;
  name?: string;
  type: 'candidate' | 'manual' | 'lead';
}

const DEFAULT_EMAIL_LAYOUT = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    * {
      box-sizing: border-box;
    }
    body {
      margin: 0;
      padding: 0;
      background-color: #f4f6f8;
      font-family: Arial, Helvetica, sans-serif;
      width: 100%;
      overflow-x: hidden;
    }
    .email-container {
      width: 100%;
      background-color: #f4f6f8;
      padding: 20px 0;
      text-align: center;
    }
    .email-content {
      width: 100%;
      max-width: 600px;
      margin: 0 auto;
      background-color: #ffffff;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 6px rgba(0,0,0,0.1);
      text-align: left; /* Reset text alignment for content */
    }
    .header {
      background-color: #2f6fed;
      color: #ffffff;
      padding: 16px 20px;
      text-align: center;
    }
    .body {
      padding: 24px 30px;
      color: #333333;
      font-size: 15px;
      line-height: 1.5;
    }
    .footer {
      background-color: #f0f2f5;
      text-align: center;
      padding: 16px;
      font-size: 12px;
      color: #777777;
    }
    /* Quill specific overrides */
    .body p { 
      margin: 0 0 0.5em 0; 
      min-height: 0.5em; 
    }
    .body p:last-child { margin-bottom: 0; }
    .body strong { font-weight: bold; }
    .body br { display: block; content: ""; margin-top: 5px; }
    .body img { max-width: 100%; height: auto; display: block; margin: 10px 0; }
    .ql-align-center { text-align: center; }
    .ql-align-right { text-align: right; }
    .ql-align-justify { text-align: justify; }
    
    /* CTA Button Styles for Editor View */
    .cta-button {
      text-decoration: none;
      display: inline-block;
      text-align: center;
      font-weight: bold;
    }
  </style>
</head>
<body>
  <div class="email-container">
    <div class="email-content">
      <div class="header">
        <h1 style="margin:0; font-size:24px;">{{layout_title}}</h1>
      </div>
      <div class="body">
        {{content}}
        {{cta_placeholder}}
      </div>
      <div class="footer">
        © 2026 JobHunter. All rights reserved.
      </div>
    </div>
  </div>
</body>
</html>
`;


// Sortable Row Component
interface SortableRowProps {
  id: string;
  children: React.ReactNode;
}

const SortableRow = ({ id, children }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    backgroundColor: isDragging ? '#f0f0f0' : 'inherit',
    zIndex: isDragging ? 2 : 1,
    position: 'relative' as 'relative', // Cast to valid Position type
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      // {...attributes} // Moving attributes to the drag handle
      // {...listeners} // Moving listeners to the drag handle
      hover
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && (child.props as any).className === 'drag-handle') {
          return React.cloneElement(child as React.ReactElement<any>, { ...attributes, ...listeners });
        }
        return child;
      })}
    </TableRow>
  );
};




// Variables fetched from API are stored in state

interface EmailsProps {
  type: string;
}

const Emails: React.FC<EmailsProps> = ({ type }) => {
  console.log("type:- ", type);
  const theme = useTheme();
  const navigate = useNavigate();

  // Navigation State
  const [view, setView] = useState<'list' | 'edit'>('list');
  const quillRef = useRef<any>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<{ name: string; value: string }[]>([]);

  React.useEffect(() => {
    fetchTemplates();
    fetchVariables();
    setView('list');
  }, [type]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const data = await emailTemplateService.getAllTemplates({ type });
      // Map _id to id for frontend compatibility
      const mappedData = data.map(t => ({ ...t, id: t._id || t.id || '' }));
      setTemplates(mappedData);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVariables = async () => {
    try {
      const data = await emailTemplateService.getVariables({ type });
      setDynamicVariables(data);
    } catch (error) {
      console.error('Error fetching variables:', error);
    }
  };

  // Button State (Integrated in layout)
  const [showCTA, setShowCTA] = useState(false);
  const [buttonConfig, setButtonConfig] = useState({
    text: 'Click Here',
    url: 'https://',
    bgColor: '#2f6fed',
    textColor: '#ffffff',
    borderRadius: '4',
    width: '200',
    height: '45',
    alignment: 'center',
    vPosition: 100, // 0-100% vertical position
    marginTop: '20',
    marginBottom: '20',
  });

  // Editor State
  const [templateTitle, setTemplateTitle] = useState('');
  const [description, setDescription] = useState('');
  const [layoutTitle, setLayoutTitle] = useState('Welcome to JobHunter 🎉');
  const [slug, setSlug] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [editorLayout, setEditorLayout] = useState<'split' | 'full-preview'>('split');
  const [reminders, setReminders] = useState<number[]>([]);
  const [newReminderDay, setNewReminderDay] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDates, setScheduledDates] = useState<Date[]>([]);
  const [scheduledTimezone, setScheduledTimezone] = useState('Asia/Calcutta');
  const [isSimpleEmail, setIsSimpleEmail] = useState(false);

  // Recipient State
  const [recipientMode, setRecipientMode] = useState<'all' | 'specific' | 'category'>('all');
  const [selectedRecipients, setSelectedRecipients] = useState<Recipient[]>([]);

  // Lead Recipient State
  const [availableLeads, setAvailableLeads] = useState<Lead[]>([]);
  const [isFetchingLeads, setIsFetchingLeads] = useState(false);
  const [leadSearchTerm, setLeadSearchTerm] = useState('');

  // Category Recipient State
  const [availableCategories, setAvailableCategories] = useState<LeadCategory[]>([]);
  const [isFetchingCategories, setIsFetchingCategories] = useState(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<LeadCategory | null>(null);

  // Variable Menu State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openVariableMenu = Boolean(anchorEl);

  // Test Email State
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Deletion State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setTemplates((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Optimistic UI update
        // Persist to backend
        const orderedIds = newItems.map(item => item.id);
        emailTemplateService.reorderTemplates(orderedIds).catch(err => {
          console.error("Failed to save order", err);
          // Optionally revert state here if needed
        });

        return newItems;
      });
    }
  };

  const handleEditClick = async (template: EmailTemplate) => {
    try {
      setSubmitLoader(true);
      const templateId = template.id || template._id;
      if (!templateId) return;

      const fullTemplate = await emailTemplateService.getTemplateById(templateId);

      // Ensure the template has a consistent ID property for handleSave
      const templateToSet = {
        ...fullTemplate,
        id: fullTemplate.id || (fullTemplate as any)._id
      };

      setSelectedTemplate(templateToSet as any);
      setTemplateTitle(fullTemplate.title);
      setDescription(fullTemplate.description || '');
      setLayoutTitle(fullTemplate.layoutTitle || 'Welcome to JobHunter 🎉');
      setSlug(fullTemplate.slug || '');
      setSubject(fullTemplate.subject);
      setContent(fullTemplate.content);
      setReminders(fullTemplate.reminders || []);

      // Button config
      if (fullTemplate.buttonConfig) {
        setButtonConfig(fullTemplate.buttonConfig);
        setShowCTA(fullTemplate.showCTA);
      } else {
        setShowCTA(false);
        setButtonConfig({
          text: 'Click Here',
          url: 'https://',
          bgColor: '#2f6fed',
          textColor: '#ffffff',
          borderRadius: '4',
          width: '200',
          height: '45',
          alignment: 'center',
          vPosition: 100,
          marginTop: '20',
          marginBottom: '20',
        });
      }

      // Recipients mapping
      setRecipientMode(fullTemplate.recipientMode || 'all');
      const loadedRecipients: Recipient[] = (fullTemplate.recipients || []).map((r: any) => {
        // If it's already a full object as per the new API response
        if (typeof r === 'object' && r !== null) {
          return {
            id: r._id || r.id,
            email: r.email_address || r.email || '',
            name: r.first_name && r.last_name ? `${r.first_name} ${r.last_name}` : (r.name || ''),
            type: 'lead'
          };
        }
        // Fallback for simple ID strings
        return { id: r, email: r, type: 'lead' };
      });
      setSelectedRecipients(loadedRecipients);

      // Category mapping (single value — accept full object or bare ID string)
      const rawCategory: any =
        (fullTemplate as any).category ??
        (fullTemplate as any).categoryId ??
        null;
      let loadedCategory: LeadCategory | null = null;
      if (rawCategory) {
        if (typeof rawCategory === 'object') {
          loadedCategory = { _id: rawCategory._id || rawCategory.id, name: rawCategory.name || '' };
        } else {
          loadedCategory = { _id: rawCategory, name: '' };
        }
      }
      setSelectedCategory(loadedCategory);
      setCategorySearchTerm('');

      setIsScheduled(!!fullTemplate.is_schedule_mail);
      setIsSimpleEmail(!!fullTemplate.is_simple_email);

      // Schedule Dates
      setScheduledDates((fullTemplate.scheduledDates || []).map(d => {
        const literalTime = d.dateTime.replace(/Z$|[+-]\d{2}(:?\d{2})?$/, '');
        return new Date(literalTime);
      }));
      setScheduledTimezone(fullTemplate.timezone || 'Asia/Calcutta');

      setView('edit');
      setPreviewMode(false);
      setFormErrors({});
      handleSearchLeads(''); // Load initial leads
      handleSearchCategories(''); // Load initial categories
    } catch (error) {
      console.error('Error fetching template details:', error);
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleAddNew = () => {
    setSelectedTemplate(null);
    setTemplateTitle('');
    setDescription('');
    setLayoutTitle('Welcome to JobHunter 🎉');
    setSlug('');
    setSubject('');
    setContent('');
    setReminders([]);
    setShowCTA(false);
    setButtonConfig({
      text: 'Click Here',
      url: 'https://',
      bgColor: '#2f6fed',
      textColor: '#ffffff',
      borderRadius: '4',
      width: '200',
      height: '45',
      alignment: 'center',
      vPosition: 100,
      marginTop: '20',
      marginBottom: '20',
    });
    setRecipientMode('all');
    setSelectedRecipients([]);
    setSelectedCategory(null);
    setCategorySearchTerm('');
    setIsScheduled(false);
    setIsSimpleEmail(false);
    setScheduledDates([]);
    setScheduledTimezone('Asia/Calcutta');
    setView('edit');
    setPreviewMode(false);
    setFormErrors({});
    handleSearchLeads(''); // Load initial leads
    handleSearchCategories(''); // Load initial categories
  };

  const handleSearchLeads = async (search: string) => {
    setIsFetchingLeads(true);
    try {
      const response = await leadService.getLeadList({
        startToken: 1,
        endToken: 50,
        search: search ? search.trim() : ''
      });
      if (response && response.data) {
        setAvailableLeads(response.data);
      }
    } catch (err) {
      console.error('Error searching leads:', err);
    } finally {
      setIsFetchingLeads(false);
    }
  };

  // Debounced lead search
  React.useEffect(() => {
    const handler = setTimeout(() => {
      handleSearchLeads(leadSearchTerm);
    }, 500);

    return () => clearTimeout(handler);
  }, [leadSearchTerm]);

  const handleSearchCategories = async (search: string) => {
    setIsFetchingCategories(true);
    try {
      const response = await leadService.getLeadCategories({
        search: search ? search.trim() : '',
        start: 0,
        limit: 100,
      });
      if (response && response.data) {
        setAvailableCategories(response.data);
      }
    } catch (err) {
      console.error('Error searching categories:', err);
    } finally {
      setIsFetchingCategories(false);
    }
  };

  // Debounced category search
  React.useEffect(() => {
    const handler = setTimeout(() => {
      handleSearchCategories(categorySearchTerm);
    }, 500);

    return () => clearTimeout(handler);
  }, [categorySearchTerm]);

  // When editing a template, the loaded category may only carry an _id (no name).
  // Once the category list arrives, hydrate the name so the dropdown + helper text
  // show the readable label instead of the bare id.
  React.useEffect(() => {
    if (!selectedCategory || selectedCategory.name) return;
    const match = availableCategories.find((c) => c._id === selectedCategory._id);
    if (match && match.name) {
      setSelectedCategory(match);
    }
  }, [availableCategories, selectedCategory]);

  const handleBackToList = () => {
    setView('list');
    setSelectedTemplate(null);
    setTemplateTitle('');
    setDescription('');
    setSlug('');
    setSubject('');
    setContent('');
    setReminders([]);
    setShowCTA(false);
    setSelectedRecipients([]);
    setSelectedCategory(null);
    setCategorySearchTerm('');
    setFormErrors({});
  };

  const handleVariableClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleVariableClose = () => {
    setAnchorEl(null);
  };

  const insertVariable = (variable: string) => {
    const formattedVar = `{{${variable}}}`;
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection();
      if (range) {
        editor.insertText(range.index, formattedVar);
      } else {
        editor.insertText(editor.getLength() - 1, formattedVar);
      }
    } else {
      setContent(prev => prev + ' ' + formattedVar);
    }
    handleVariableClose();
  };

  // handleInsertButton and buttonDialogOpen are removed as per instructions.

  // handleInsertCursorButton is removed in favor of visual positioning

  const handleReminderToggle = (value: number) => {
    setReminders(prev =>
      prev.includes(value)
        ? prev.filter(r => r !== value)
        : [...prev, value].sort((a, b) => a - b)
    );
  };

  const handleAddCustomReminder = () => {
    const day = parseInt(newReminderDay);
    if (!isNaN(day) && day > 0 && !reminders.includes(day)) {
      setReminders(prev => [...prev, day].sort((a, b) => a - b));
      setNewReminderDay('');
    }
  };

  const handleAddSchedule = () => {
    setScheduledDates(prev => [...prev, new Date()]);
  };

  const handleRemoveSchedule = (index: number) => {
    setScheduledDates(prev => prev.filter((_, i) => i !== index));
  };

  const handleDateChange = (index: number, newDate: Date | null) => {
    if (newDate) {
      setScheduledDates(prev => {
        const next = [...prev];
        next[index] = newDate;
        return next;
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!templateTitle.trim()) {
      errors.templateTitle = 'Template Name is required';
    }

    if (!slug.trim()) {
      errors.slug = 'Unique Identifier (Slug) is required';
    }

    if (!subject.trim()) {
      errors.subject = 'Subject is required';
    }

    if (!content.trim() || content === '<p><br></p>') {
      errors.content = 'Email Content is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const templateData = {
      title: templateTitle,
      description,
      slug,
      subject,
      content,
      layoutTitle,
      reminders,
      showCTA,
      buttonConfig: showCTA ? buttonConfig : undefined,
      recipientMode,
      recipients: recipientMode === 'specific' ? selectedRecipients.map(r => r.id) : [],
      categoryId: recipientMode === 'category' && selectedCategory ? selectedCategory._id : '',
      fullHtml: getTemplateHtml(), // Insert full HTML
      type: type, // Add type from props
      is_schedule_mail: isScheduled,
      is_simple_email: isSimpleEmail,
      scheduledDates: isScheduled ? scheduledDates.map(d => ({ dateTime: format(d, "yyyy-MM-dd'T'HH:mm:ss") })) : [],
      timezone: isScheduled ? scheduledTimezone : undefined,
    };

    console.log('Saving template data:', templateData);

    try {
      setSubmitLoader(true);
      if (selectedTemplate && selectedTemplate.id) {
        await emailTemplateService.updateTemplate(selectedTemplate.id, templateData as any);
      } else {
        await emailTemplateService.createTemplate({ ...templateData, title: templateData.title || subject } as any);
      }
      await fetchTemplates();
      handleBackToList();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleDeleteClick = (template: EmailTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      setSubmitLoader(true);
      await emailTemplateService.deleteTemplate(templateToDelete.id);
      await fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    } finally {
      setSubmitLoader(false);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleSendEmail = () => {
    // ... existing logic ...
  };

  const handleOpenTestEmailDialog = () => {
    setTestEmailOpen(true);
  };

  const handleCloseTestEmailDialog = () => {
    setTestEmailOpen(false);
    setTestEmailAddress('');
  };

  const handleSendTestEmailConfirm = async () => {
    if (!testEmailAddress) {
      alert('Please enter an email address');
      return;
    }

    setIsSendingTest(true);
    try {
      const htmlContent = getTemplateHtml();
      await emailTemplateService.sendTestEmail(testEmailAddress, subject, htmlContent);
      // alert(`Test email sent to ${testEmailAddress}`);
      handleCloseTestEmailDialog();
    } catch (error) {
      console.error('Failed to send test email:', error);
      // alert('Failed to send test email');
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleRemoveRecipient = (id: string) => {
    setSelectedRecipients(prev => prev.filter(r => r.id !== id));
  };

  // handleCandidateSelect removed as per simplified architecture

  const handleLeadSelect = (leads: Lead[]) => {
    const leadRecipients = leads.map(lead => ({
      id: lead._id || String(Math.random()),
      email: lead.email_address,
      name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
      type: 'lead' as const
    }));

    setSelectedRecipients(prev => {
      const otherRecipients = prev.filter(r => r.type !== 'lead');
      return [...otherRecipients, ...leadRecipients];
    });
  };



  const getTemplateHtml = () => {
    let renderedContent = content;

    // 1. Logic to inject visual CTA button based on vPosition (Only if not simple email)
    if (showCTA) {
      const { text, url, bgColor, textColor, borderRadius, width, height, alignment, vPosition, marginTop, marginBottom } = buttonConfig;

      const buttonTable = `
        <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin-top: ${marginTop}px; margin-bottom: ${marginBottom}px;">
          <tr>
            <td align="${alignment}">
              <table border="0" cellspacing="0" cellpadding="0">
                <tr>
                  <td align="center" style="border-radius: ${borderRadius}px;" bgcolor="${bgColor}">
                    <a href="${url}" target="_blank" style="font-size: 16px; font-family: Arial, sans-serif; color: ${textColor}; text-decoration: none; border-radius: ${borderRadius}px; padding: 0 20px; border: 1px solid ${bgColor}; display: inline-block; font-weight: bold; line-height: ${height}px; width: ${width}px; text-align: center;">
                      ${text}
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      `;

      // Split content into paragraphs or breaks to find insertion point
      const blocks = renderedContent.split(/(<\/p>|<br\s*\/?>)/i).filter(b => b.trim() !== "");

      if (blocks.length > 0) {
        // Calculate insertion index
        const insertIndex = Math.floor((vPosition / 100) * blocks.length);

        // Handle edge cases (top/bottom)
        if (insertIndex === 0) {
          renderedContent = buttonTable + renderedContent;
        } else if (insertIndex >= blocks.length) {
          renderedContent = renderedContent + buttonTable;
        } else {
          // Insert at the calculated block
          const newContent: string[] = [];
          blocks.forEach((block, idx) => {
            newContent.push(block);
            if (idx === insertIndex) {
              newContent.push(buttonTable);
            }
          });
          renderedContent = newContent.join("");
        }
      } else {
        // Fallback for empty or single-line content
        renderedContent = renderedContent + buttonTable;
      }
    }

    if (isSimpleEmail) {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body { 
              font-family: Arial, sans-serif; 
              font-size: 16px; 
              line-height: 1.6; 
              color: #333333; 
              padding: 20px;
              margin: 0;
            }
            p { margin-bottom: 1em; }
          </style>
        </head>
        <body>
          <div class="content">${renderedContent}</div>
        </body>
        </html>
      `;
    }

    const finalHtml = DEFAULT_EMAIL_LAYOUT
      .replace('{{layout_title}}', layoutTitle)
      .replace('{{content}}', renderedContent)
      .replace('{{cta_placeholder}}', ''); // Placeholder is effectively ignored now

    return finalHtml;
  };

  const renderLivePreview = () => {
    const finalHtml = getTemplateHtml();

    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          minHeight: '600px',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: '#f4f6f8',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box sx={{ p: 1, backgroundColor: alpha(theme.palette.primary.main, 0.05), borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PreviewIcon fontSize="small" color="primary" />
          <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
            Live Preview
          </Typography>
        </Box>
        <iframe
          // srcDoc={finalHtml}
          srcDoc={finalHtml.replace(/&nbsp;/g, ' ')}
          title="Email Preview"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
            backgroundColor: 'transparent'
          }}
        />
      </Box>
    );
  };

  const formatDateTime = (dateTime: any) => {
    if (!dateTime) return '';

    // Ensure the date string is interpreted as UTC to preserve literal time
    // If it doesn't have a Z or offset, append Z
    const utcDateTime = (typeof dateTime === 'string' && !dateTime.includes('Z') && !dateTime.includes('+') && !dateTime.includes('-'))
      ? `${dateTime}Z`
      : dateTime;

    return new Date(utcDateTime).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC", // keeps original time
    });
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        {
          view === 'edit' ?
            <IconButton onClick={() => view === 'edit' ? handleBackToList() : navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            : null
        }
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            flexGrow: 1,
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            },
          }}
        >
          {view === 'list' ? 'Email Templates' : selectedTemplate ? `Edit: ${selectedTemplate.title}` : 'Add New Template'}
        </Typography>
        {view === 'list' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ borderRadius: 2 }}
          >
            Add New Template
          </Button>
        )}
      </Box>

      {view === 'list' ? (
        /* List View */
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '50px' }}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Template Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Subject Preview</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Recipient Management</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Scheduling</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={templates.map(t => t.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {templates.length > 0 ? (
                      templates.map((template) => (
                        <SortableRow key={template.id} id={template.id}>
                          {/* Drag Handle Column - Moved to first position */}
                          <TableCell className="drag-handle" style={{ width: '50px', cursor: 'grab' }}>
                            <DragIndicatorIcon color="action" />
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="600">{template.title}</Typography>
                            {template.description && (
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', maxWidth: '200px' }}>
                                {template.description}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                              ID: {template.slug}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary" sx={{
                              maxWidth: '300px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {template.subject}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const mode = (template as any).recipientMode || 'all';
                              const labelMap: Record<string, { label: string; icon: React.ReactElement }> = {
                                all: { label: 'All Leads', icon: <AllIcon sx={{ fontSize: 14 }} /> },
                                specific: { label: 'Specific Leads', icon: <SpecificIcon sx={{ fontSize: 14 }} /> },
                                category: { label: 'By Category', icon: <CategoryIcon sx={{ fontSize: 14 }} /> },
                              };
                              const { label, icon } = labelMap[mode] || labelMap.all;
                              return (
                                <Chip
                                  icon={icon}
                                  label={label}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            {template.is_schedule_mail ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: 'secondary.main', gap: 0.5 }}>
                                  <ScheduleIcon sx={{ fontSize: 16 }} />
                                  <Typography variant="caption" fontWeight="bold">Scheduled</Typography>
                                </Box>
                                {template.scheduledDates && template.scheduledDates.length > 0 ? (
                                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                    {template.scheduledDates.length === 1
                                      ? formatDateTime(template.scheduledDates[0].dateTime)
                                      // ? format(new Date(template.scheduledDates[0].dateTime), 'MMM d, h:mm a')
                                      : `${template.scheduledDates.length} slots starting ${formatDateTime(template.scheduledDates[0].dateTime)}`
                                    }
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>No slots set</Typography>
                                )}
                                {template.timezone && (
                                  <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                    {timeZoneList[template.timezone as keyof typeof timeZoneList]?.label || template.timezone}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled">Manual</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditClick(template)}
                              sx={{ borderRadius: 2, mr: 1 }}
                            >
                              Edit & Preview
                            </Button>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(template)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </SortableRow>
                      ))
                    ) : (
                      !isLoading && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                              <Box
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  p: 2,
                                  borderRadius: '50%',
                                  mb: 2,
                                  color: theme.palette.primary.main
                                }}
                              >
                                <ListIcon sx={{ fontSize: 40 }} />
                              </Box>
                              <Typography variant="h6" color="text.primary" gutterBottom>
                                No email templates found
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 3, maxWidth: 400 }}>
                                You haven't created any email templates yet. Create your first template to start sending automated emails to candidates.
                              </Typography>
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddNew}
                                sx={{ borderRadius: 2 }}
                              >
                                Add New Template
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </SortableContext>
                </DndContext>
              </TableBody>
            </Table>
          </TableContainer>
          {isLoading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.background.paper, 0.7),
              zIndex: 2,
              borderRadius: 3
            }}>
              <CircularProgress color="primary" />
            </Box>
          )}
        </Paper>
      ) : (
        /* Editor View */
        <Card elevation={3} sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Template Content
                </Typography>
              </Box>
              <Box>
                <Button
                  variant={editorLayout === 'full-preview' ? 'contained' : 'outlined'}
                  startIcon={<PreviewIcon />}
                  onClick={() => setEditorLayout(editorLayout === 'full-preview' ? 'split' : 'full-preview')}
                  sx={{ mr: 1 }}
                  color={editorLayout === 'full-preview' ? 'secondary' : 'primary'}
                >
                  {editorLayout === 'full-preview' ? 'Exit Full Preview' : 'Full Preview Mode'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={handleOpenTestEmailDialog}
                  sx={{ mr: 1 }}
                >
                  Send Test Email
                </Button>
                <Button
                  variant="contained"
                  startIcon={submitLoader ? <CircularProgress size={20} color="inherit" /> : <CopyIcon />}
                  onClick={handleSave}
                  disabled={submitLoader}
                >
                  {submitLoader ? 'Saving...' : 'Save Template'}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Internal Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Give a brief internal description of this email"
                  helperText="Used to identify the purpose of this email internally"
                  sx={{ mb: 2 }}
                  multiline
                  rows={2}
                />
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Unique Identifier (Slug)"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="e.g., registration_email"
                  helperText="Unique ID for system usage"
                  disabled={!!selectedTemplate}
                />
              </Grid>
              <Grid item xs={12} md={6}>
              </Grid> */}
              {editorLayout === 'split' && (
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderColor: alpha(theme.palette.primary.main, 0.1),
                      backgroundColor: alpha(theme.palette.primary.main, 0.01),
                      borderRadius: 2,
                      mb: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <SpecificIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle2" fontWeight="700">
                        Recipient Management
                      </Typography>
                    </Box>

                    <RadioGroup
                      row
                      value={recipientMode}
                      onChange={(e) => setRecipientMode(e.target.value as any)}
                      sx={{ mb: 2 }}
                    >
                      <FormControlLabel
                        value="all"
                        control={<Radio size="small" />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AllIcon fontSize="small" color="action" />
                            <Typography variant="body2">All Leads</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="specific"
                        control={<Radio size="small" />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <SpecificIcon fontSize="small" color="action" />
                            <Typography variant="body2">Specific Leads</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="category"
                        control={<Radio size="small" />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CategoryIcon fontSize="small" color="action" />
                            <Typography variant="body2">By Category</Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>

                    {recipientMode === 'specific' && (
                      <Box>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          {/* Lead Selector - High performance for 200+ Leads */}
                          <Grid item xs={12}>
                            <Autocomplete
                              multiple
                              freeSolo
                              size="small"
                              filterOptions={(x) => x}
                              options={availableLeads}
                              onInputChange={(_, newInputValue) => {
                                setLeadSearchTerm(newInputValue);
                              }}
                              value={selectedRecipients
                                .filter(r => r.type === 'lead')
                                .map(r => ({
                                  _id: r.id,
                                  email_address: r.email,
                                  first_name: r.name?.split(' ')[0] || '',
                                  last_name: r.name?.split(' ').slice(1).join(' ') || '',
                                })) as Lead[]
                              }
                              isOptionEqualToValue={(option, value) => option._id === value._id}
                              getOptionLabel={(option) => typeof option === 'string' ? option : `${option.first_name || ''} ${option.last_name || ''} (${option.email_address || ''})`}
                              onChange={(_, val) => handleLeadSelect(val as any)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Type to search leads..."
                                  variant="outlined"
                                  autoComplete="off"
                                />
                              )}
                              renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => (
                                  <Chip
                                    label={`${option.first_name || ''} ${option.last_name || ''}`}
                                    {...getTagProps({ index })}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                ))
                              }
                            />
                          </Grid>

                        </Grid>

                        <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedRecipients.length > 0 ? (
                            selectedRecipients.map((r) => (
                              <Chip
                                key={r.id}
                                label={r.name || r.email}
                                size="small"
                                onDelete={() => handleRemoveRecipient(r.id)}
                                color={r.type === 'lead' ? 'secondary' : 'default'}
                                variant="outlined"
                                icon={r.type === 'lead' ? <VariableIcon fontSize="small" /> : <AllIcon fontSize="small" />}
                                sx={{ borderRadius: '6px' }}
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', px: 1 }}>
                              No leads selected. Use the search box above or select "All Leads".
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}

                    {recipientMode === 'category' && (
                      <Box>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12}>
                            <Autocomplete
                              size="small"
                              filterOptions={(x) => x}
                              options={availableCategories}
                              loading={isFetchingCategories}
                              onInputChange={(_, newInputValue, reason) => {
                                if (reason === 'reset') return;
                                setCategorySearchTerm(newInputValue);
                              }}
                              value={selectedCategory}
                              isOptionEqualToValue={(option, value) => option._id === value._id}
                              getOptionLabel={(option) => option.name || ''}
                              onChange={(_, val) => setSelectedCategory(val as LeadCategory | null)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Type to search a category..."
                                  variant="outlined"
                                  autoComplete="off"
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {isFetchingCategories ? (
                                          <CircularProgress color="inherit" size={18} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

                        <Box sx={{ py: 1, px: 1 }}>
                          {selectedCategory ? (
                            <Typography variant="caption" color="textSecondary">
                              Email will be sent to every lead in{' '}
                              <strong>
                                {selectedCategory.name || (isFetchingCategories ? 'Loading…' : 'this category')}
                              </strong>
                              .
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                              No category selected. Pick one from the search above.
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}

                    {recipientMode === 'all' && (
                      <Box sx={{ py: 1.5, px: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2, border: `1px dashed ${alpha(theme.palette.success.main, 0.3)}` }}>
                        <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'medium' }}>
                          <AllIcon fontSize="small" />
                          This email will be sent to <strong>all leads</strong> in your database.
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* Subject and CTA Configuration Panel */}
              {editorLayout === 'split' && (
                <Grid item xs={12} md={6} style={{ height: 700, overflow: 'auto', paddingRight: 8 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Template Name"
                        value={templateTitle}
                        onChange={(e) => {
                          setTemplateTitle(e.target.value);
                          if (formErrors.templateTitle) {
                            setFormErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.templateTitle;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder="e.g., Welcome Email"
                        helperText={formErrors.templateTitle || "Internal name for this template"}
                        error={!!formErrors.templateTitle}
                        sx={{ mb: 2 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Unique Identifier (Slug)"
                        value={slug}
                        onChange={(e) => {
                          setSlug(e.target.value);
                          if (formErrors.slug) {
                            setFormErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.slug;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder="e.g., registration_email"
                        helperText={formErrors.slug || "Unique ID for system usage"}
                        error={!!formErrors.slug}
                        disabled={!!selectedTemplate} // Disable slug editing if needed, or allow it
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Email Subject"
                            value={subject}
                            onChange={(e) => {
                              setSubject(e.target.value);
                              if (formErrors.subject) {
                                setFormErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.subject;
                                  return newErrors;
                                });
                              }
                            }}
                            variant="outlined"
                            placeholder="Enter subject line..."
                            helperText={formErrors.subject || "You can use {{variables}} in the subject too."}
                            error={!!formErrors.subject}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Layout Header Title"
                            value={layoutTitle}
                            onChange={(e) => setLayoutTitle(e.target.value)}
                            variant="outlined"
                            placeholder="e.g. Welcome to JobHunter 🎉"
                            helperText="You can use {{variables}} in the subject too."
                            sx={{ mb: 2, mt: 2 }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* CTA Button Configuration Panel - Moved here */}
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderColor: alpha(theme.palette.secondary.main, 0.2),
                          backgroundColor: alpha(theme.palette.secondary.main, 0.01),
                          borderRadius: 2,
                          mb: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showCTA ? 2 : 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ButtonIcon color="secondary" sx={{ mr: 1, fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight="700">
                              Integrated Call-to-Action Button
                            </Typography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={showCTA}
                                onChange={(e) => setShowCTA(e.target.checked)}
                                color="secondary"
                              />
                            }
                            label={<Typography variant="caption" fontWeight="medium">Enable Button</Typography>}
                          />
                        </Box>

                        {showCTA && (
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Button Text"
                                value={buttonConfig.text}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, text: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Button Link"
                                value={buttonConfig.url}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, url: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Margin Top"
                                value={buttonConfig.marginTop}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, marginTop: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Margin Bottom"
                                value={buttonConfig.marginBottom}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, marginBottom: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Width"
                                value={buttonConfig.width}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, width: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Height"
                                value={buttonConfig.height}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, height: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Radius"
                                value={buttonConfig.borderRadius}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, borderRadius: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Alignment</InputLabel>
                                <Select
                                  value={buttonConfig.alignment}
                                  label="Alignment"
                                  onChange={(e) => setButtonConfig({ ...buttonConfig, alignment: e.target.value as any })}
                                >
                                  <MenuItem value="left">Left</MenuItem>
                                  <MenuItem value="center">Center</MenuItem>
                                  <MenuItem value="right">Right</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                              <Box>
                                <Typography variant="caption" color="textSecondary" display="block">BG Color</Typography>
                                <input
                                  type="color"
                                  value={buttonConfig.bgColor}
                                  onChange={(e) => setButtonConfig({ ...buttonConfig, bgColor: e.target.value })}
                                  style={{ width: '100%', height: '32px', padding: 0, border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box>
                                <Typography variant="caption" color="textSecondary" display="block">Text Color</Typography>
                                <input
                                  type="color"
                                  value={buttonConfig.textColor}
                                  onChange={(e) => setButtonConfig({ ...buttonConfig, textColor: e.target.value })}
                                  style={{ width: '100%', height: '32px', padding: 0, border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ px: 1 }}>
                                <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                                  Vertical Position ({buttonConfig.vPosition === 0 ? 'Top' : buttonConfig.vPosition === 100 ? 'Bottom' : `${buttonConfig.vPosition}%`})
                                </Typography>
                                <Slider
                                  size="small"
                                  value={buttonConfig.vPosition}
                                  onChange={(_, val) => setButtonConfig({ ...buttonConfig, vPosition: val as number })}
                                  color="secondary"
                                  valueLabelDisplay="auto"
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </Paper>
                    </Grid>

                    {/* Variables & Editor Toolbar */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                        <Typography variant="body2" color="textSecondary">Insert:</Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VariableIcon />}
                          onClick={handleVariableClick}
                          sx={{ textTransform: 'none' }}
                        >
                          Dynamic Variable
                        </Button>
                        <Menu
                          anchorEl={anchorEl}
                          open={openVariableMenu}
                          onClose={handleVariableClose}
                        >
                          {dynamicVariables.map((v) => (
                            <MenuItem key={v.value} onClick={() => insertVariable(v.value)}>
                              <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
                              <ListItemText primary={v.name} secondary={`{{${v.value}}}`} />
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>

                      <Box sx={{ mb: 2, width: '100%' }}>
                        <Typography variant="body2" color={formErrors.content ? "error" : "textSecondary"} sx={{ mb: 1, fontWeight: formErrors.content ? '600' : 'normal' }}>
                          Email Content {formErrors.content && `(${formErrors.content})`}
                        </Typography>
                        <Box sx={{
                          border: formErrors.content ? `1.5px solid ${theme.palette.error.main}` : 'none',
                          borderRadius: '12px',
                          overflow: 'hidden'
                        }}>
                          <RichTextEditor
                            ref={quillRef}
                            value={content}
                            onChange={(val: string) => {
                              setContent(val);
                              if (formErrors.content) {
                                setFormErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.content;
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder="Draft your email content here..."
                          />
                        </Box>
                        {formErrors.content && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', ml: 1 }}>
                            {formErrors.content}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Reminder Settings */}
                    {/* <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                          backgroundColor: alpha(theme.palette.primary.main, 0.01),
                          borderRadius: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <ReminderIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                          <Typography variant="subtitle2" fontWeight="700">
                            Follow-up Auto-Reminders
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          Select when to automatically resend this email if the job seeker remains inactive.
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          {reminders.length > 0 ? (
                            reminders.map((day) => (
                              <Chip
                                key={day}
                                label={`${day} Days`}
                                onDelete={() => handleReminderToggle(day)}
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: '500' }}
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                              No reminders set. Add some below.
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="Day"
                            value={newReminderDay}
                            onChange={(e) => setNewReminderDay(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCustomReminder()}
                            sx={{ width: '80px' }}
                            InputProps={{ inputProps: { min: 1 } }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleAddCustomReminder}
                            disabled={!newReminderDay || parseInt(newReminderDay) <= 0}
                          >
                            Add Reminder
                          </Button>
                          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                          <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>Quick select:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {[1, 3, 5, 7].map(d => (
                              <Chip
                                key={d}
                                label={`+${d}d`}
                                size="small"
                                onClick={() => !reminders.includes(d) && handleReminderToggle(d)}
                                disabled={reminders.includes(d)}
                                sx={{ cursor: 'pointer', height: '24px' }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Paper>
                    </Grid> */}

                    {/* Email Scheduling Section */}
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderColor: alpha(theme.palette.secondary.main, 0.2),
                          backgroundColor: alpha(theme.palette.secondary.main, 0.01),
                          borderRadius: 2,
                          mt: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ScheduleIcon color="secondary" sx={{ mr: 1, fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight="700">
                              Email Scheduling
                            </Typography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={isScheduled}
                                onChange={(e) => setIsScheduled(e.target.checked)}
                                color="secondary"
                              />
                            }
                            label={<Typography variant="caption" fontWeight="medium">Enable Scheduling</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={isSimpleEmail}
                                onChange={(e) => setIsSimpleEmail(e.target.checked)}
                                color="info"
                              />
                            }
                            label={<Typography variant="caption" fontWeight="medium">Simple Email (No Layout)</Typography>}
                          />
                        </Box>

                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          Schedule this email to be sent at specific dates and times.
                        </Typography>

                        {isScheduled && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Timezone Selector */}
                            <FormControl fullWidth size="small">
                              <InputLabel id="timezone-select-label">Scheduling Timezone</InputLabel>
                              <Select
                                labelId="timezone-select-label"
                                value={scheduledTimezone}
                                label="Scheduling Timezone"
                                onChange={(e) => setScheduledTimezone(e.target.value)}
                              >
                                {Object.entries(timeZoneList).map(([key, tz]: [string, any]) => (
                                  <MenuItem key={key} value={key}>
                                    {tz.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            {scheduledDates.map((date, index) => (
                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DateTimePicker
                                  label={`Schedule Slot ${index + 1}`}
                                  value={date}
                                  onChange={(newDate) => handleDateChange(index, newDate)}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                    }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveSchedule(index)}
                                  sx={{
                                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                    borderRadius: 1
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={handleAddSchedule}
                              color="secondary"
                              sx={{ alignSelf: 'flex-start', mt: 1 }}
                            >
                              Add Schedule Slot
                            </Button>
                          </Box>
                        )}

                        {!isScheduled && (
                          <Box sx={{ py: 1.5, textAlign: 'center', border: `1px dashed ${alpha(theme.palette.text.disabled, 0.2)}`, borderRadius: 1 }}>
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                              Scheduling is currently disabled. Emails will be sent immediately.
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {/* Preview Side */}
              <Grid item xs={12} md={editorLayout === 'full-preview' ? 12 : 6} style={{ height: 700, overflow: 'auto', paddingRight: 8 }}>
                <Box sx={{ position: editorLayout === 'split' ? 'sticky' : 'static', top: 20 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Email Preview {editorLayout === 'full-preview' && '(Full Desktop View)'}
                    </Typography>
                    {editorLayout === 'full-preview' && (
                      <Button size="small" onClick={() => setEditorLayout('split')} startIcon={<EditIcon />}>
                        Return to Side-by-Side
                      </Button>
                    )}
                  </Box>
                  {renderLivePreview()}
                </Box>
              </Grid>
            </Grid>
          </CardContent >
        </Card >
      )}

      {/* Test Email Dialog */}
      <Dialog open={testEmailOpen} onClose={handleCloseTestEmailDialog}>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent sx={{ pt: 2, minWidth: 400 }}>
          <Typography variant="body2" color="textSecondary" paragraph>
            Enter an email address to receive a preview of this template.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Recipient Email"
            type="email"
            fullWidth
            variant="outlined"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestEmailDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSendTestEmailConfirm} variant="contained" disabled={isSendingTest}>
            {isSendingTest ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the template <strong>"{templateToDelete?.title}"</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={submitLoader}
            startIcon={submitLoader ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {submitLoader ? 'Deleting...' : 'Delete Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global Submit Loader */}
      <Backdrop
        sx={{ color: '#fff', zIndex: 9999 }}
        open={submitLoader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box >
  );
};

export default Emails;
